// Property-panel editor for the `chatbot` block.
//
// The chatbot block places an end-user AI assistant on a smart page. The
// runtime /chat endpoint does the actual grounding + LLM call (see
// runtime/server/twasta_runtime_server/routers/chat.py); this editor only
// configures *scope* (which page/data the bot may read) and *presentation*
// (title, greeting, suggested questions, height).
//
// Field map (ChatbotBlock — see @twasta/page-schema):
//   title              → text input
//   greeting           → textarea (first assistant message)
//   systemPrompt       → textarea (advanced; overrides default instructions)
//   groundOnPage       → checkbox
//   dataSources        → list of ObjectPickerField (RBAC-gated at runtime)
//   suggestedQuestions → list of text inputs (quick-start chips)
//   height             → number input

import { Plus, Trash2 } from 'lucide-react'

import type { ChatbotBlock } from './types'
import ObjectPickerField from './ObjectPickerField'

interface Props {
  block: ChatbotBlock
  // Catalog used to populate the data-source picker. The picker also allows
  // custom names, so authors can reference dashboards/visuals/objects not in
  // this list (everything is RBAC-gated at runtime regardless).
  transactions: { obj_name: string; title: string }[]
  onChange: (next: ChatbotBlock) => void
}

export function ChatbotBlockEditor({ block, transactions, onChange }: Props) {
  const set = (patch: Partial<ChatbotBlock>) => onChange({ ...block, ...patch })

  // ── data sources (obj_names the bot may read) ──
  const dataSources = block.dataSources ?? []
  const addDataSource = () => set({ dataSources: [...dataSources, ''] })
  const updateDataSource = (idx: number, name: string) => {
    const next = [...dataSources]
    next[idx] = name
    set({ dataSources: next })
  }
  const removeDataSource = (idx: number) =>
    set({ dataSources: dataSources.filter((_, i) => i !== idx) })

  // ── suggested questions (quick-start chips) ──
  const questions = block.suggestedQuestions ?? []
  const addQuestion = () => set({ suggestedQuestions: [...questions, ''] })
  const updateQuestion = (idx: number, text: string) => {
    const next = [...questions]
    next[idx] = text
    set({ suggestedQuestions: next })
  }
  const removeQuestion = (idx: number) =>
    set({ suggestedQuestions: questions.filter((_, i) => i !== idx) })

  const catalog = transactions.map((t) => ({ obj_name: t.obj_name, title: t.title }))

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Field label="Title">
        <input
          type="text"
          value={block.title ?? ''}
          onChange={(e) => set({ title: e.target.value })}
          placeholder="Assistant"
          style={inputStyle}
        />
      </Field>

      <Field label="Greeting message">
        <textarea
          value={block.greeting ?? ''}
          onChange={(e) => set({ greeting: e.target.value })}
          placeholder="Hi! Ask me anything about this page or your data."
          rows={2}
          style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
        />
        <p style={hintStyle}>Shown as the first assistant message when the chat loads. Leave blank for none.</p>
      </Field>

      <label style={checkboxRow}>
        <input
          type="checkbox"
          checked={block.groundOnPage !== false}
          onChange={(e) => set({ groundOnPage: e.target.checked })}
        />
        <span>Include this page's text content as context</span>
      </label>

      {/* ── Data sources ── */}
      <div style={sectionStyle}>
        <header style={sectionHeader}>
          <strong style={{ fontSize: 13 }}>Data sources ({dataSources.length})</strong>
          <button type="button" onClick={addDataSource} style={addBtn}>
            <Plus size={12} /> Add
          </button>
        </header>
        <p style={hintStyle}>
          Project objects (transactions, dashboards, visuals) the bot may read and reference.
          Each is RBAC-gated — the bot can only surface data the viewing user is allowed to see.
        </p>
        {dataSources.length === 0 ? (
          <div style={emptyStyle}>No data sources. The bot will only reference the page content.</div>
        ) : (
          dataSources.map((objName, idx) => (
            <div key={idx} style={rowStyle}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ObjectPickerField
                  value={objName}
                  onChange={(next) => updateDataSource(idx, next)}
                  catalog={catalog}
                  typeLabel="object"
                  emptyHint="No transactions in this project yet — use a custom name."
                  allowCustom
                />
              </div>
              <button
                type="button"
                onClick={() => removeDataSource(idx)}
                style={iconBtn}
                title="Remove data source"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))
        )}
      </div>

      {/* ── Suggested questions ── */}
      <div style={sectionStyle}>
        <header style={sectionHeader}>
          <strong style={{ fontSize: 13 }}>Suggested questions ({questions.length})</strong>
          <button type="button" onClick={addQuestion} style={addBtn}>
            <Plus size={12} /> Add
          </button>
        </header>
        <p style={hintStyle}>Quick-start prompts shown as clickable chips before the user's first message.</p>
        {questions.length === 0 ? (
          <div style={emptyStyle}>No suggested questions.</div>
        ) : (
          questions.map((q, idx) => (
            <div key={idx} style={rowStyle}>
              <input
                type="text"
                value={q}
                onChange={(e) => updateQuestion(idx, e.target.value)}
                placeholder={`Question ${idx + 1}`}
                style={inputStyle}
              />
              <button
                type="button"
                onClick={() => removeQuestion(idx)}
                style={iconBtn}
                title="Remove question"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))
        )}
      </div>

      {/* ── Advanced ── */}
      <Field label="System prompt (advanced)">
        <textarea
          value={block.systemPrompt ?? ''}
          onChange={(e) => set({ systemPrompt: e.target.value || undefined })}
          placeholder="Leave blank to use the default assistant instructions."
          rows={3}
          style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 13, resize: 'vertical' }}
        />
        <p style={hintStyle}>Overrides the default instructions entirely. Leave blank for the default behavior.</p>
      </Field>

      <Field label="Height (px)">
        <input
          type="number"
          min={200}
          max={1200}
          value={block.height ?? 420}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10)
            if (!Number.isFinite(v)) return
            set({ height: v })
          }}
          style={inputStyle}
        />
      </Field>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
const iconBtn: React.CSSProperties = {
  background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
  borderRadius: 4, padding: 4, cursor: 'pointer', color: 'var(--color-text-secondary)',
  display: 'inline-flex', alignItems: 'center', flexShrink: 0, alignSelf: 'flex-start',
}
const addBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 8px', fontSize: 13,
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-bg-secondary)', cursor: 'pointer', color: 'var(--color-text)',
}
const hintStyle: React.CSSProperties = {
  margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5,
}
const emptyStyle: React.CSSProperties = {
  fontSize: 13, color: 'var(--color-text-secondary)',
  padding: 10, textAlign: 'center', background: 'var(--color-bg)',
  borderRadius: 6, border: '1px dashed var(--color-border)',
}
const sectionStyle: React.CSSProperties = {
  borderTop: '1px solid var(--color-border)', paddingTop: 10, display: 'grid', gap: 8,
}
const sectionHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
}
const rowStyle: React.CSSProperties = {
  display: 'flex', gap: 6, alignItems: 'flex-start',
}
const checkboxRow: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-text)',
}
